import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const ROUTES = {
  LOGIN: {
    route: {
      nl: 'aanmelden',
      fr: 'se-connecter',
    },
  },
  LOGIN_VO_TEACHER: {
    route: {
      nl: 'leerkracht',
      fr: 'professeur',
    },
  },
  LOGIN_CALLBACK: {
    route: {
      nl: 'auth-callback',
      fr: 'auth-callback',
    },
  },
  CONNECT_CALLBACK: {
    route: {
      nl: 'connect-callback',
      fr: 'connect-callback',
    },
  },
  DISCONNECT_CALLBACK: {
    route: {
      nl: 'disconnect-callback',
      fr: 'disconnect-callback',
    },
  },
  EDIT_CALLBACK: {
    route: {
      nl: 'edit-callback',
      fr: 'edit-callback',
    },
  },
  MY_METHODS: {
    route: {
      nl: 'lesmethodes',
      fr: 'methodes-pedagogiques',
    },
  },
  'MY_METHODS.DASHBOARD': {
    route: {
      nl: 'dashboard',
      fr: 'tableau-de-bord',
    },
  },
  'MY_METHODS.CATALOGUE': {
    route: {
      nl: 'catalogus',
      fr: 'catalogue',
    },
  },
  MY_GROUPS: {
    route: {
      nl: 'klassen',
      fr: 'classes',
    },
  },
  'MY_GROUPS.PREVIOUS_SCHOOLYEAR': {
    route: {
      nl: 'vorig-schooljaar',
      fr: 'annee-scolaire-precedente',
    },
  },
  'MY_GROUPS.CURRENT_SCHOOLYEAR': {
    route: {
      nl: 'huidig-schooljaar',
      fr: 'annee-scolaire-en-cours',
    },
  },
  'MY_GROUPS.NEXT_SCHOOLYEAR': {
    route: {
      nl: 'volgend-schooljaar',
      fr: 'prochaine-annee-scolaire',
    },
  },
  MY_TEACHERS: {
    route: {
      nl: 'leerkrachten',
      fr: 'professeurs',
    },
  },
  'MY_TEACHERS.CURRENT_SCHOOLYEAR': {
    route: {
      nl: 'huidig-schooljaar',
      fr: 'annee-scolaire-en-cours',
    },
  },
  'MY_TEACHERS.PREVIOUS_SCHOOLYEAR': {
    route: {
      nl: 'vorig-schooljaar',
      fr: 'annee-scolaire-precedente',
    },
  },
  MY_PROFILE: {
    route: {
      nl: 'profiel',
      fr: 'profil',
    },
  },
  'MY_PROFILE.PERSONAL_DETAILS': {
    route: {
      nl: 'gegevens',
      fr: 'donnees-personnelles',
    },
  },
  'MY_PROFILE.LICENSES': {
    route: {
      nl: 'licenties',
      fr: 'licences',
    },
  },
  HELP: {
    route: {
      nl: 'info-hulp',
      fr: 'support',
    },
  },
  PRIVACY_POLICY: {
    route: {
      nl: 'privacybeleid',
      fr: 'politique-de-confidentialite',
    },
  },
  TERMS_AND_CONDITIONS: {
    route: {
      nl: 'algemene-voorwaarden',
      fr: 'conditions-generales',
    },
  },
  MATERIAL: {
    route: {
      nl: ':subjectSlug/:methodSlug/:moduleSlug',
      fr: ':subjectSlug/:methodSlug/:moduleSlug',
    },
  },
  'MATERIAL.CATEGORICAL': {
    route: {
      nl: 'lesmateriaal',
      fr: 'materiel-pedagogique',
    },
  },
  'MATERIAL.CATEGORICAL.BROWSE': {
    route: {
      nl: ':materialCategory',
      fr: ':materialCategory',
    },
  },
  'MATERIAL.CONTENT': {
    route: {
      nl: 'leerinhoud',
      fr: 'contenus-dapprentissage',
    },
  },
  'MATERIAL.CONTENT.TOC': {
    route: {
      nl: ':selectedModuleSlug/:nodeId?',
      fr: ':selectedModuleSlug/:nodeId?',
    },
  },
  'MATERIAL.ASSIGNMENTS': {
    route: {
      nl: 'taken',
      fr: 'taches',
    },
  },
  'MATERIAL.ASSIGNMENTS.PLANNING': {
    route: {
      nl: 'planning',
      fr: 'programmees',
    },
  },
  'MATERIAL.ASSIGNMENTS.ARCHIVE': {
    route: {
      nl: 'archief',
      fr: 'archivees',
    },
  },
  'MATERIAL.ASSIGNMENTS.TO_MAKE': {
    route: {
      nl: 'te-maken',
      fr: 'a-realiser',
    },
  },
  'MATERIAL.ASSIGNMENTS.FINISHED': {
    route: {
      nl: 'ingediend',
      fr: 'soumises',
    },
  },
  'MATERIAL.RESULTS': {
    route: {
      nl: 'resultaten',
      fr: 'resultats',
    },
  },
  'MATERIAL.RESULTS.BY_STUDENT': {
    route: {
      nl: 'per-leerling',
      fr: 'par-élève',
    },
  },
  'MATERIAL.RESULTS.BY_ITEM': {
    route: {
      nl: 'per-oefening-taak',
      fr: 'par-exercise-tache',
    },
  },
  'MATERIAL.SEARCH': {
    route: {
      nl: 'zoeken',
      fr: 'rechercher',
    },
  },
  'MATERIAL.VEM': {
    route: {
      nl: 'oefenen-volgens-oefenthema',
      fr: 'sentrainer-plus',
    },
  },
  'MATERIAL.VEM.CATEGORY_DETAIL': {
    route: {
      nl: ':selectedCategoryId',
      fr: ':selectedCategoryId',
    },
  },
  SEARCH: {
    route: {
      nl: ':moduleSlug/zoeken',
      fr: ':moduleSlug/rechercher',
    },
  },
  INVITE: {
    route: {
      nl: 'invite/:id',
      fr: 'invite/:id',
    },
  },
  DIRECT_MATERIAL_LINK: {
    route: {
      nl: 'link/:superModule/:selectedModule/:nodeId/:medialinkId?',
      fr: 'link/:superModule/:selectedModule/:nodeId/:medialinkId?',
    },
  },
  LINKED_MATERIAL_LINK: {
    route: {
      nl: 'material/link',
      fr: 'material/link',
    },
  },
  QR_CODES_LINK: {
    route: {
      nl: 'go/:id',
      fr: 'go/:id',
    },
  },
} as const;

const OVERRIDES = {
  VO: {
    MY_GROUPS: {
      route: {
        nl: 'cursussen',
        fr: 'cours',
      },
    },
  },
} as const;

export const IANUA_BASE_PATH = '/ianua';

//TODO: Clean up before ianua goes live
export const QR_DEMO_BASE_PATH = '/qr-demo';

export function getRoute(route: keyof typeof ROUTES, language: SupportedUserLanguage) {
  const studioGroup = process.env.REACT_APP_P_STUDIO_GROUP;
  if (studioGroup === 'VO') {
    return (
      OVERRIDES[studioGroup][route as keyof typeof OVERRIDES[typeof studioGroup]]?.route[language] ||
      ROUTES[route].route[language]
    );
  }

  return ROUTES[route].route[language];
}

export function buildRelativePath(
  route: keyof typeof ROUTES,
  language: SupportedUserLanguage,
  params: Record<string, string> = {},
) {
  const basePath = getRoute(route, language);

  return Object.entries(params).reduce<string>((path, [key, value]) => path.replace(`:${key}`, value), basePath);
}

export function buildAbsolutePath(
  routes: (keyof typeof ROUTES)[],
  language: SupportedUserLanguage,
  params: Record<string, string> = {},
  overrideBasePath: string = IANUA_BASE_PATH,
) {
  const basePath = routes.map(route => getRoute(route, language as SupportedUserLanguage)).join('/');

  return `${overrideBasePath}/${Object.entries(params).reduce<string>(
    (path, [key, value]) => path.replace(`:${key}`, value),
    basePath,
  )}`;
}

export function useRoutes() {
  const {
    i18n: { language },
  } = useTranslation();

  /**
   * @description Get the translated route for a key.
   */
  const getRouteWrapped = useCallback(
    (route: keyof typeof ROUTES) => getRoute(route, language as SupportedUserLanguage),
    [language],
  );

  /**
   * @description Build relative path based on a route key. Used to build a path to navigate to.
   */
  const buildRelativePathWrapped = useCallback(
    (route: keyof typeof ROUTES, params: Record<string, string> = {}) => {
      return buildRelativePath(route, language as SupportedUserLanguage, params);
    },
    [language],
  );

  /**
   * @description Build absolute path based on a route keys. Used to build a path to navigate to.
   */
  const buildAbsolutePathWrapped = useCallback(
    (routes: (keyof typeof ROUTES)[], params: Record<string, string> = {}, basePath?: string) => {
      return buildAbsolutePath(routes, language as SupportedUserLanguage, params, basePath);
    },
    [language],
  );

  return {
    getRoute: getRouteWrapped,
    buildAbsolutePath: buildAbsolutePathWrapped,
    buildRelativePath: buildRelativePathWrapped,
  };
}
